import { useRef, useState } from "react";
import { studioAPI, tryParseJSON } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { CHARACTER } from "@/constants/testIds";
import { toast } from "sonner";
import { Cat, ArrowRight, Copy, Image as ImageIcon, Loader2, Upload, X } from "lucide-react";

export default function CharacterKit() {
    const { lang } = useApp();
    const [name, setName] = useState("Gaston");
    const [species, setSpecies] = useState(lang === "fr" ? "gros chat roux" : "big ginger cat");
    const [traits, setTraits] = useState(lang === "fr" ? "paresseux, sarcastique, adore la sieste" : "lazy, sarcastic, nap lover");
    const [universe, setUniverse] = useState(lang === "fr" ? "appartement parisien" : "parisian apartment");
    const [busy, setBusy] = useState(false);
    const [imgBusy, setImgBusy] = useState(false);
    const [imgSrc, setImgSrc] = useState(null);
    const [referenceImg, setReferenceImg] = useState(null);
    const [raw, setRaw] = useState("");
    const fileRef = useRef(null);
    const parsed = tryParseJSON(raw);

    const run = async () => {
        setBusy(true); setRaw(""); setImgSrc(null);
        try {
            const { result } = await studioAPI.character({ name, species, traits, universe, language: lang });
            setRaw(result);
        } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
        finally { setBusy(false); }
    };

    const handleFile = (file) => {
        if (!file) return;
        if (file.size > 4 * 1024 * 1024) { toast.error(lang === "fr" ? "Fichier > 4MB" : "File > 4MB"); return; }
        const reader = new FileReader();
        reader.onload = () => setReferenceImg(reader.result);
        reader.readAsDataURL(file);
    };

    const genImage = async () => {
        const prompt = parsed?.master_prompt || `${name}, ${species}, ${traits}, cartoon style, 9:16 portrait, warm palette`;
        setImgBusy(true);
        try {
            const { image, used_reference } = await studioAPI.characterImage({ prompt, reference_image: referenceImg });
            setImgSrc(image);
            toast.success(used_reference ? (lang === "fr" ? "Image cohérente ✨" : "Consistent image ✨") : (lang === "fr" ? "Image générée ✨" : "Image generated ✨"));
        } catch (e) { toast.error(e?.response?.data?.detail || "Image error"); }
        finally { setImgBusy(false); }
    };

    return (
        <div data-testid={CHARACTER.container} className="grid lg:grid-cols-5 gap-6">
            <div className="lg:col-span-2 space-y-4">
                <div>
                    <div className="overline">03 · Character</div>
                    <h2 className="font-display text-2xl font-black uppercase mt-1">{lang === "fr" ? "Kit personnage" : "Character kit"}</h2>
                    <p className="text-sm text-slate-400 mt-2">{lang === "fr" ? "Un personnage cohérent, épisode après épisode." : "One consistent character, episode after episode."}</p>
                </div>
                {[
                    { label: lang === "fr" ? "Nom" : "Name", v: name, s: setName, id: CHARACTER.name },
                    { label: lang === "fr" ? "Type / espèce" : "Species", v: species, s: setSpecies, id: CHARACTER.species },
                    { label: lang === "fr" ? "Univers" : "Universe", v: universe, s: setUniverse, id: CHARACTER.universe },
                ].map((f) => (
                    <div key={f.label}>
                        <label className="text-xs uppercase tracking-widest text-slate-500">{f.label}</label>
                        <input data-testid={f.id} value={f.v} onChange={(e) => f.s(e.target.value)}
                            className="mt-2 w-full bg-[#0F141C] border border-[#253046] rounded px-3 py-2.5 text-sm focus:outline-none focus:border-[#FF4D00]" />
                    </div>
                ))}
                <div>
                    <label className="text-xs uppercase tracking-widest text-slate-500">{lang === "fr" ? "Traits" : "Traits"}</label>
                    <textarea data-testid={CHARACTER.traits} value={traits} onChange={(e) => setTraits(e.target.value)} rows={3}
                        className="mt-2 w-full bg-[#0F141C] border border-[#253046] rounded px-3 py-2.5 text-sm focus:outline-none focus:border-[#FF4D00] resize-none" />
                </div>

                <div>
                    <label className="text-xs uppercase tracking-widest text-slate-500 flex items-center gap-2">
                        <ImageIcon className="w-3.5 h-3.5" /> {lang === "fr" ? "Photo de référence (optionnel)" : "Reference photo (optional)"}
                    </label>
                    <div className="mt-2">
                        <input
                            ref={fileRef}
                            type="file"
                            accept="image/png,image/jpeg,image/webp"
                            className="hidden"
                            data-testid="character-reference-input"
                            onChange={(e) => handleFile(e.target.files?.[0])}
                        />
                        {referenceImg ? (
                            <div className="relative inline-block">
                                <img src={referenceImg} alt="reference" data-testid="character-reference-preview" className="w-24 h-32 object-cover rounded border border-[#FF4D00]/40" />
                                <button
                                    onClick={() => setReferenceImg(null)}
                                    data-testid="character-reference-clear"
                                    className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-black border border-[#FF4D00] grid place-items-center text-white hover:bg-[#FF4D00] hover:text-black kinetic"
                                >
                                    <X className="w-3 h-3" />
                                </button>
                                <div className="text-[10px] font-mono-sf text-[#10B981] mt-1">
                                    {lang === "fr" ? "Cohérence activée" : "Consistency ON"}
                                </div>
                            </div>
                        ) : (
                            <button
                                onClick={() => fileRef.current?.click()}
                                data-testid="character-reference-btn"
                                className="w-full px-3 py-2.5 rounded border-2 border-dashed border-[#3B4B6C] hover:border-[#FF4D00] bg-[#0F141C] text-slate-400 hover:text-[#FF4D00] flex items-center justify-center gap-2 text-xs kinetic"
                            >
                                <Upload className="w-3.5 h-3.5" />
                                {lang === "fr" ? "Uploader une photo pour garder le même visage" : "Upload a photo to keep the same face"}
                            </button>
                        )}
                    </div>
                </div>

                <button data-testid={CHARACTER.generateBtn} onClick={run} disabled={busy}
                    className="w-full flame-gradient text-black font-semibold py-3 rounded flex items-center justify-center gap-2 kinetic flame-glow disabled:opacity-60">
                    {busy ? "···" : <>{lang === "fr" ? "Générer la fiche" : "Generate sheet"} <ArrowRight className="w-4 h-4" /></>}
                </button>
            </div>

            <div data-testid={CHARACTER.result} className="lg:col-span-3 rounded border border-[#253046] bg-[#0B111B] p-5 min-h-[500px]">
                {!raw && !busy && (
                    <div className="h-full flex flex-col items-center justify-center text-center py-20">
                        <Cat className="w-10 h-10 text-slate-700 mb-3" />
                        <p className="text-sm text-slate-500">{lang === "fr" ? "La fiche personnage complète apparaîtra ici." : "The full character sheet will appear here."}</p>
                    </div>
                )}
                {busy && <div className="h-full flex items-center justify-center text-slate-400 font-mono-sf text-xs">DESIGNING CHARACTER ···</div>}
                {parsed && (
                    <div className="space-y-5">
                        <div className="flex items-start gap-4">
                            {imgSrc ? (
                                <img src={imgSrc} alt={parsed.name} data-testid="character-image-render"
                                    className="w-32 aspect-[9/16] object-cover rounded border border-[#FF4D00]/40 flame-glow shrink-0" />
                            ) : (
                                <button
                                    onClick={genImage}
                                    disabled={imgBusy}
                                    data-testid={CHARACTER.imageBtn}
                                    className="w-32 aspect-[9/16] rounded border-2 border-dashed border-[#3B4B6C] hover:border-[#FF4D00] bg-[#0F141C] text-slate-400 hover:text-[#FF4D00] flex flex-col items-center justify-center gap-2 kinetic text-xs shrink-0 disabled:opacity-60"
                                >
                                    {imgBusy ? <><Loader2 className="w-5 h-5 animate-spin" /><span>Nano Banana</span></> : <><ImageIcon className="w-6 h-6" /><span>{lang === "fr" ? "Générer image" : "Generate image"}</span></>}
                                </button>
                            )}
                            <div className="flex-1">
                                <div className="overline">Character</div>
                                <div className="font-display text-3xl font-black uppercase mt-1">{parsed.name}</div>
                                {parsed.one_liner && <p className="text-sm text-slate-300 mt-2 italic">« {parsed.one_liner} »</p>}
                                {imgSrc && (
                                    <button onClick={genImage} disabled={imgBusy} className="mt-3 text-xs text-slate-400 hover:text-[#FF4D00] flex items-center gap-1">
                                        {imgBusy ? <Loader2 className="w-3 h-3 animate-spin" /> : <ImageIcon className="w-3 h-3" />}
                                        {lang === "fr" ? "Régénérer" : "Regenerate"}
                                    </button>
                                )}
                            </div>
                        </div>

                        {parsed.palette && Array.isArray(parsed.palette) && (
                            <div>
                                <div className="overline">Palette</div>
                                <div className="mt-2 flex gap-2">
                                    {parsed.palette.map((c, i) => (
                                        <div key={i} className="flex-1">
                                            <div className="h-14 rounded" style={{ background: c }} />
                                            <div className="font-mono-sf text-[10px] text-slate-400 mt-1 text-center">{c}</div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        <div className="grid sm:grid-cols-2 gap-3">
                            {["appearance", "personality", "backstory", "voice_style"].map((k) => parsed[k] && (
                                <div key={k} className="bg-[#161D2A] border border-[#253046] rounded p-3">
                                    <div className="overline">{k.replace("_", " ")}</div>
                                    <div className="text-sm text-slate-300 mt-1">{parsed[k]}</div>
                                </div>
                            ))}
                        </div>

                        {parsed.expressions && (
                            <div>
                                <div className="overline">Expressions</div>
                                <div className="mt-2 flex flex-wrap gap-1.5">
                                    {parsed.expressions.map((e, i) => <span key={i} className="text-xs px-2 py-1 rounded bg-[#FFB800]/10 border border-[#FFB800]/30 text-[#FFB800]">{e}</span>)}
                                </div>
                            </div>
                        )}
                        {parsed.poses && (
                            <div>
                                <div className="overline">Poses</div>
                                <div className="mt-2 flex flex-wrap gap-1.5">
                                    {parsed.poses.map((e, i) => <span key={i} className="text-xs px-2 py-1 rounded bg-[#0099FF]/10 border border-[#0099FF]/30 text-[#0099FF]">{e}</span>)}
                                </div>
                            </div>
                        )}

                        {parsed.master_prompt && (
                            <div className="bg-gradient-to-r from-[#FF4D00]/10 to-transparent border-l-2 border-[#FF4D00] rounded-r p-4">
                                <div className="flex items-center justify-between">
                                    <div className="overline">Master prompt (reusable)</div>
                                    <button onClick={() => { navigator.clipboard.writeText(parsed.master_prompt); toast.success("Copié"); }} className="text-xs text-slate-400 hover:text-white flex items-center gap-1"><Copy className="w-3 h-3" /></button>
                                </div>
                                <p className="text-sm text-slate-200 mt-2 leading-relaxed">{parsed.master_prompt}</p>
                            </div>
                        )}

                        {parsed.consistency_rules && (
                            <div>
                                <div className="overline">Consistency rules</div>
                                <ul className="mt-2 space-y-1">
                                    {parsed.consistency_rules.map((r, i) => (
                                        <li key={i} className="text-sm text-slate-300 flex gap-2">
                                            <span className="font-mono-sf text-[10px] text-[#FF4D00] mt-1">·</span>
                                            <span>{r}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}
                    </div>
                )}
                {raw && !parsed && <pre className="text-xs text-slate-300 whitespace-pre-wrap">{raw}</pre>}
            </div>
        </div>
    );
}
